import React, { useEffect, useMemo, useState } from 'react';
import { useMapView, MAP_VIEW_CLASSIC } from '../map-view/MapViewContext';
import { useEncounter } from './EncounterContext';
import { useGameAudio } from '../game/audio/GameAudioContext';
import { ENCOUNTER_COPY } from './copy';
import { encounterGpsFixState, isEncounterActionInRange } from './core.mjs';
import '../styles/encounters.css';

function countdown(expiresAt, now) {
  const seconds = Math.max(0, Math.ceil((Number(expiresAt || 0) - now) / 1000));
  return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
}

export default function EncounterOverlay() {
  const { view } = useMapView();
  const context = useEncounter();
  const { playEncounterDiscovery, stopEncounterSound } = useGameAudio();
  const discovery = context.encounter;
  useEffect(() => {
    if (!context.popupOpen || discovery?.status !== 'discovered') return undefined;
    void playEncounterDiscovery(discovery, context.discoveryReason);
    return () => stopEncounterSound(discovery.id);
  }, [discovery?.id, discovery?.status, context.popupOpen, context.discoveryReason, playEncounterDiscovery, stopEncounterSound]);
  const [now, setNow] = useState(Date.now());
  const [dismissedSpawnNotice, setDismissedSpawnNotice] = useState('');
  useEffect(() => {
    if (!context.spawnNotice) setDismissedSpawnNotice('');
  }, [context.spawnNotice]);
  useEffect(() => {
    if (!context.encounter) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [context.encounter]);

  const encounter = context.encounter;
  const copy = encounter ? ENCOUNTER_COPY[encounter.type] : null;
  const trail = encounter?.payload?.trail;
  const trailProgress = Number(trail?.progress || 0);
  const trailCount = Array.isArray(trail?.points) ? trail.points.length : 0;
  const inRange = isEncounterActionInRange(encounter, context.distance);
  const gps = encounterGpsFixState(context.latestGpsFix, now);
  const relocations = Number(encounter?.payload?.relocations || 0);
  const relocationLimit = encounter?.debug ? 10 : 2;
  const canRelocate = Boolean(encounter && relocations < relocationLimit);
  const walkability = encounter?.payload?.walkability;
  const stats = walkability?.stats || {};
  const rejected = Object.values(stats.rejected || {}).reduce((sum, value) => sum + Number(value || 0), 0);
  const action = useMemo(() => {
    if (!encounter || encounter.status !== 'active') return null;
    if (encounter.type === 'treibgut') return { label: copy.action, disabled: !inRange, run: context.finish };
    if (encounter.type === 'verlorene_buddel') return { label: copy.action, disabled: !inRange, run: () => context.act('catch') };
    if (encounter.type === 'pfandspur') {
      const atGoal = trailCount > 0 && trailProgress === trailCount - 1;
      return { label: atGoal ? copy.action : 'Nächsten Marker sichern', disabled: !inRange, run: atGoal ? context.finish : () => context.act('advance') };
    }
    return null;
  }, [context, copy?.action, encounter, inRange, trailCount, trailProgress]);

  if (!context.enabled) return null;
  return (
    <>
      {encounter && context.popupOpen && encounter.status === 'discovered' && (
        <div className="pfp-encounter-modal" role="dialog" aria-modal="true" aria-labelledby="pfp-encounter-title">
          <div className="pfp-encounter-modal__card">
            <img src={encounter.popupImage} alt="" loading="eager" />
            <div className="pfp-encounter-modal__content">
              <span className="pfp-encounter-kicker">Zufällige Piratenbegegnung</span>
              <h2 id="pfp-encounter-title">{encounter.title}</h2>
              <p>{encounter.subtitle}</p>
              <div className="pfp-encounter-modal__meta"><span>ca. {Math.round(context.distance)} m</span><span>{countdown(encounter.expiresAt, now)}</span></div>
              {encounter.debug && <small>Admin-Visualtest: Rewards sind deaktiviert.</small>}
              <button type="button" onClick={context.start} disabled={context.pending}>Begegnung starten</button>
              {canRelocate && <button type="button" className="pfp-encounter-secondary" onClick={context.relocate} disabled={context.pending}>Ort nicht erreichbar</button>}
              <button type="button" className="pfp-encounter-secondary" onClick={context.dismiss} disabled={context.pending}>Weiterziehen</button>
              {encounter.debug && walkability && (
                <details className="pfp-encounter-debug">
                  <summary>Walkability-Debug</summary>
                  <dl>
                    <dt>Feature</dt><dd>{walkability.featureType || '–'} · {walkability.featureId || '–'}</dd>
                    <dt>Luft / Weg</dt><dd>{walkability.airDistance} m / {walkability.routeDistance} m</dd>
                    <dt>Faktor / Score</dt><dd>{walkability.routeFactor} / {walkability.score}</dd>
                    <dt>Kandidaten</dt><dd>{stats.candidate_count ?? '–'} ({stats.walkable_feature_count ?? '–'} Wege)</dd>
                    <dt>Rejected</dt><dd>{rejected || '–'}</dd>
                    <dt>Graph</dt><dd>{stats.node_count ?? '–'} Knoten / {stats.edge_count ?? '–'} Kanten</dd>
                    <dt>Cache / Zeit</dt><dd>{walkability.providerCache} / {stats.resolver_ms ?? '–'} ms (Routing {stats.routing_ms ?? '–'} ms)</dd>
                  </dl>
                </details>
              )}
            </div>
          </div>
        </div>
      )}

      {encounter?.status === 'active' && (
        <section className="pfp-encounter-hud" aria-live="polite" data-encounter-type={encounter.type}>
          <div><strong>{encounter.type === 'pfandspur' ? '🧭' : '🏴‍☠️'} {encounter.title}</strong><span>{Math.round(context.distance)} m · {countdown(encounter.expiresAt, now)}</span></div>
          {trailCount > 0 && <span>Marker {Math.min(trailProgress + 1, trailCount)} / {trailCount}</span>}
          {canRelocate && <button type="button" className="pfp-encounter-relocate" onClick={context.relocate} disabled={context.pending}>Ort nicht erreichbar</button>}
          {action && (
            <div className="pfp-encounter-action">
              <button type="button" onClick={action.run} disabled={context.pending || context.gpsRefreshing || action.disabled}>
                {action.disabled
                  ? `Noch ${Math.max(1, Math.ceil(context.distance - Number(encounter.minActivationDistance || 15)))} m`
                  : context.gpsRefreshing
                    ? 'Standort wird aktualisiert …'
                    : gps.usable
                      ? action.label
                      : encounter.type === 'treibgut' ? 'GPS aktualisieren & bergen' : 'GPS aktualisieren'}
              </button>
              {!action.disabled && !gps.usable && !context.gpsRefreshing && (
                <small>{gps.state === 'inaccurate' ? 'GPS-Signal zu ungenau' : 'Aktueller GPS-Fix erforderlich'}</small>
              )}
            </div>
          )}
          {encounter.debug && (
            <details className="pfp-encounter-debug pfp-encounter-debug--gps">
              <summary>GPS-Debug</summary>
              <dl>
                <dt>Accuracy</dt><dd>{Number.isFinite(Number(gps.accuracy)) ? `${Number(gps.accuracy).toFixed(1)} m` : '–'}</dd>
                <dt>Fix age</dt><dd>{Number.isFinite(Number(gps.ageMs)) ? `${Math.max(0, Number(gps.ageMs) / 1000).toFixed(1)} s` : '–'}</dd>
                <dt>Encounter</dt><dd>{Number.isFinite(context.distance) ? `${context.distance.toFixed(1)} m` : '–'}</dd>
                <dt>Action GPS</dt><dd>{context.gpsRefreshing ? 'refreshing' : gps.usable ? `valid (${gps.state})` : gps.state}</dd>
                <dt>Letzte Action</dt><dd>{context.lastActionGps ? `${Number(context.lastActionGps.accuracy).toFixed(1)} m · ${(Number(context.lastActionGps.ageMs) / 1000).toFixed(1)} s · ${Number(context.lastActionGps.distance).toFixed(1)} m` : '–'}</dd>
                <dt>Server</dt><dd>{context.serverRejection?.reason || '–'}{Number.isFinite(Number(context.serverRejection?.receivedAgeSeconds)) ? ` · ${Number(context.serverRejection.receivedAgeSeconds).toFixed(1)} s` : ''}</dd>
              </dl>
            </details>
          )}
        </section>
      )}

      {view === MAP_VIEW_CLASSIC && encounter?.status === 'active' && (
        <div className="pfp-encounter-2d-marker" aria-label={`2D-Marker: ${encounter.title}`}>
          <span aria-hidden="true">{encounter.type === 'pfandspur' ? '🧭' : encounter.type === 'treibgut' ? '🪵' : '🍾'}</span>
          <div><strong>{encounter.title}</strong><small>{Math.round(context.distance)} m entfernt</small></div>
        </div>
      )}

      {(context.notice || context.error) && (
        <button type="button" className={`pfp-encounter-toast ${context.error ? 'is-error' : ''}`} onClick={context.clearNotice}>{context.error || context.notice}</button>
      )}
      {!encounter && !context.error && !context.notice && context.spawnNotice && context.spawnNotice !== dismissedSpawnNotice && (
        <aside className="pfp-encounter-search-status" aria-label="Begegnungssuche">
          <span role="status">{context.spawnNotice}</span>
          <button type="button" aria-label="Suchhinweis schließen" onClick={() => setDismissedSpawnNotice(context.spawnNotice)}>×</button>
        </aside>
      )}
    </>
  );
}
